import { FeatureFlag, ProviderRole } from '@bariendo/lib/constants';
import type { ConferenceStartAddTarget, TwilioConferenceSession } from '@bariendo/lib/twilio-conference-session';
import { Communication, Practitioner } from '@medplum/fhirtypes';
import { Call } from '@twilio/voice-sdk';
import { createContext } from 'react';
import { AccountPhoneNumber } from '../services/twilio';
import type { ProviderDirectory } from '../services/provider-directory';
import { IncomingCall, OutgoingCall, ActiveCallUi } from './TwilioPhoneProvider';

export interface CurrentUserContextValue {
  currentUser: Practitioner | undefined;
  currentUserRole: ProviderRole | undefined;
  permissionsLoading: boolean;
  currentUserPermissions: {
    isAdmin: boolean;
    isPhysicianOnly: boolean;
    hasAdminMembership: boolean;
    canAccessRestrictedContent: boolean;
    canWriteMedications: boolean;
    canWriteLabs: boolean;
    canAccessPartners: boolean;
    canManageSchedules: boolean;
    canConfigureIvr: boolean;
    canEditOnCallSchedule: boolean;
    canAccessPayments: boolean;
    /** Order, ledger and billing rows, read-only. canAccessPayments plus Finance (BAR-2648). */
    canViewPayments: boolean;
    /** The Intake pill on /Patient: INTAKE_HUB_ROLES plus administrators (BAR-2788). */
    canViewIntakeHub: boolean;
    /** The Coverage pill on /Patient: COVERAGE_HUB_ROLES plus administrators (BAR-3093). */
    canViewCoverageHub: boolean;
    /** The Finance ops page: Finance and administrators (BAR-3024). */
    canViewFinanceOps: boolean;
    canViewCiManagerDashboard: boolean;
    canCreatePatientAccessNote: boolean;
    /** Encounter notes, prescriptions, orders and results. False for Patient Access (BAR-2585). */
    canViewClinicalRecord: boolean;
    /** Calls, SMS, chat and notes — the timeline's Communication record. False for Finance (BAR-2648). */
    canViewConversations: boolean;
    /** Record Submitted / Approved / Denied on an employer benefit. CC and admins (BAR-2593). */
    canRecordBenefitEvents: boolean;
    /** Edit the insurance card through `update-insurance-coverage`: the bot's own rule (BAR-2932). */
    canEditInsuranceCard: boolean;
    canEditNote: (note: Communication) => boolean;
  };
}

export const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

export interface ProviderDirectoryContextValue {
  directory: ProviderDirectory;
  /** Every Practitioner with a PractitionerRole, as the roster search returned them. */
  practitioners: Practitioner[];
  loading: boolean;
}

/** Null outside the provider: avatars then render with no role ring rather than throwing. */
export const ProviderDirectoryContext = createContext<ProviderDirectoryContextValue | null>(null);

/** Set when Accept / Reject came from a push: the call that push was for (BAR-2531). */
export interface CallNotificationActionOptions {
  notificationCallSid?: string;
}

export interface TwilioPhoneState {
  connectionStatus: 'offline' | 'connecting' | 'online' | 'in-call';
  currentCall: Call | null;
  incomingCall: IncomingCall | null;
  outgoingCall: OutgoingCall | null;
  isOutgoingCall: boolean;
  callStartTime: Date | null;
  isInCallFromOtherTab: boolean;
  availableCallerIds: AccountPhoneNumber[];
  conferenceSession: TwilioConferenceSession | null;
  conferenceSessionError: string | null;
  canManageConference: boolean;
  activeCallUi: ActiveCallUi | null;
  isRemotePartyConnected: boolean;

  // Connection controls
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;

  // Call controls
  makeCall: (phoneNumber: string, callerId?: string, recipient?: string) => Promise<void>;
  acceptCall: (options?: CallNotificationActionOptions) => Promise<void>;
  rejectCall: (options?: CallNotificationActionOptions) => Promise<void>;
  hangupCall: () => Promise<void>;

  refreshConferenceSession: () => Promise<void>;
  startConferenceAdd: (target: ConferenceStartAddTarget) => Promise<void>;
  mergeConferenceCall: () => Promise<void>;
  cancelConferenceConsult: () => Promise<void>;
  reconnectPrimaryConference: () => Promise<void>;
  dropConferenceParticipant: (participantCallSid: string) => Promise<void>;

  // Utility
  callDuration: string;
}

export const TwilioPhoneContext = createContext<TwilioPhoneState | null>(null);

export interface FeatureFlagsState {
  flagsMap: Record<FeatureFlag, boolean>;
  isEnabled: (flag: FeatureFlag) => boolean;
}

export const FeatureFlagsContext = createContext<FeatureFlagsState | null>(null);
